import { IColumn, IContextualMenuItem } from '@fluentui/react';
import { IVerticalBarChartDataPoint, VerticalBarChart } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPActionButton, IPIconButton, IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import { useWidgetDrillDownNavigate } from 'components/Dashboard/WidgetDrillDownContext';
import LibraryPicker from 'components/Pickers/LibraryPicker';
import { deleteIcon, globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { AudienceDTO } from 'models/dto/audience/audienceDTO';
import { PublicationResultDTO } from 'models/dto/resourceLinkDTO';
import { Widget61DTO } from 'models/dto/widgets/widget61DTO';
import ResourceLink from 'models/resourceLink';
import ResourceList, { ResourceListType } from 'models/resourceList';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataLibraryReadResponse } from 'services/Api/dashboardService';
import { apiGetLink, apiGetLinks } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { getGraphSharepointErrorMsg } from 'services/Graph/graphErrors';
import AppError from 'utils/appError';
import { fromApiDateTimeOptional, toLocaleDateTimeMedium } from 'utils/datetime';
import { sortOnString, sortOnVersion } from 'utils/sorting';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetLibraryReadResponseConfig {
  linkId: number;

  constructor() {
    this.linkId = 0;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.linkId = newRawConfig.linkId ?? 0;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetLibraryReadResponseConfig {
    const newConfig = new WidgetLibraryReadResponseConfig();
    newConfig.linkId = this.linkId;

    return newConfig;
  }
}

interface IWidgetLibraryReadResponseProps extends IWidgetRendererProps {}

const WidgetLibraryReadResponse: FunctionComponent<IWidgetLibraryReadResponseProps> = (
  props: IWidgetLibraryReadResponseProps,
) => {
  const { t } = useTranslation(['translation', 'widgets', 'publication']);
  const appContext = useContext(AppContext);
  const navigate = useWidgetDrillDownNavigate();
  const [link, setLink] = useState<ResourceLink | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedVersion, setSelectedVersion] = useState<PublicationResultDTO | undefined>(undefined);
  const [data, setData] = useState<Widget61DTO | undefined>(undefined);
  const [err, setErr] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetLibraryReadResponseConfig => {
    const newConfig = new WidgetLibraryReadResponseConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      setErr(undefined);
      const config = loadConfig();

      if (config && config.linkId) {
        //get link
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const link = await apiGetLink(config.linkId, accessToken, appContext.globalDataCache);
        if (link) {
          const data = await apiGetWidgetDataLibraryReadResponse(link, accessToken, props.abortSignal);

          setData(data);

          //init with latest version
          if (data.publicationResults && data.publicationResults.length > 0) {
            data.publicationResults.sort((a, b) => sortOnVersion(b.newVersion, a.newVersion));
            setSelectedVersion(data.publicationResults[0]);
          }
        }

        setLink(link);
      }
    } catch (err) {
      const appErr = AppError.fromGraphError(err);
      const graphErr = getGraphSharepointErrorMsg(appErr, t);
      if (graphErr) {
        setErr(graphErr);
      } else {
        appContext.setError(appErr);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onClick = (audience: AudienceDTO) => {
    if (!hasUserFeatureGenericManager(appContext)) return;
    //when user selected a previous version, show message that we cannot drill down
    if (selectedVersion && data && data.publicationResults) {
      const latestVersion = data.publicationResults.reduce((prev, current) =>
        sortOnVersion(prev.newVersion, current.newVersion) > 0 ? prev : current,
      );
      if (sortOnVersion(selectedVersion.newVersion, latestVersion.newVersion) < 0) {
        appContext.showNotification(t('widgets:LibraryReadResponse.CannotDrillDownPreviousVersion'), false, true);

        return;
      }
    }
    const url = `/library/publication#report@${audience.audienceId}|${link?.linkId}`;
    navigate(url);
  };

  const getData = (): IVerticalBarChartDataPoint[] => {
    if (
      !data ||
      !data.userReadConfirmations ||
      data.userReadConfirmations?.length === 0 ||
      !data.audiences ||
      data.audiences?.length === 0
    )
      return [];

    //filter audiences that have read confirmations
    const audiencesToShow = data.audiences.filter((a) => {
      return data.userReadConfirmations?.some((urc) => urc.audienceIds?.idList?.includes(a.audienceId.toString()));
    });
    audiencesToShow.sort((a, b) => sortOnString(a.trans[0]?.name, b.trans[0]?.name));

    //map to datapoints
    const points = audiencesToShow.map((d) => {
      //get the read confirmations for this audience
      const confirmations = data.userReadConfirmations?.filter(
        (urc) => urc.audienceIds?.idList?.includes(d.audienceId.toString()),
      );
      //count all vs read for the selected version
      const allForVersion = confirmations?.filter((c) => c.requestedVersion === selectedVersion?.newVersion);
      const readForVersion = allForVersion?.filter(
        (c) => c.version === selectedVersion?.newVersion && c.readConfirmed !== undefined,
      );
      const percentage = ((readForVersion?.length ?? 0) / (allForVersion?.length || 1)) * 100;

      const item: IVerticalBarChartDataPoint = {
        x: d.trans[0]?.name || '',
        y: percentage || 0.0001, //cannot be zero else the bar does not show
        yAxisCalloutData: `${Math.round(percentage)}%`,
        onClick: () => onClick(d),
      };

      return item;
    });

    return points;
  };

  const getVersionOptions = (): IContextualMenuItem[] => {
    if (!data?.publicationResults) return [];

    return data.publicationResults.map((v) => {
      return {
        key: v.publicationId.toString(),
        text: `${v.newVersion} (${toLocaleDateTimeMedium(fromApiDateTimeOptional(v.created))})`,
        data: v,
        onClick: () => {
          setSelectedVersion(v);
        },
      };
    });
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (err) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{err}</IPText>
      </IPStack>
    );
  }

  if (!link) {
    //config is invalid
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:LibraryReadResponse.Config.Invalid')}</IPText>
      </IPStack>
    );
  }

  const points = getData();
  const hasData = points.length > 0;

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal horizontalAlign="space-between" verticalAlign="center">
          <IPStack.Item>
            <IPText styles={globalTextStylesBold}>{link.linkName}</IPText>
          </IPStack.Item>
          <IPStack.Item>
            <IPActionButton
              text={t('widgets:LibraryReadResponse.Version', { version: selectedVersion?.newVersion })}
              menuProps={{
                items: getVersionOptions(),
              }}
              styles={{ root: { height: 16 } }}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      {!hasData && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPText>{t('widgets:LibraryReadResponse.NoData')}</IPText>
        </IPStack>
      )}
      {hasData && (
        <IPStack.Item grow>
          <VerticalBarChart
            data={points}
            wrapXAxisLables={true}
            hideLegend={true}
            enableGradient={true}
            barWidth={'auto'}
            yMinValue={0}
            yMaxValue={100}
            yAxisTickFormat={(value: number) => `${Math.round(value)}%`}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default WidgetLibraryReadResponse;

//
// Config
//

interface IWidgetConfigLibraryReadResponseProps extends IWidgetConfigRendererProps {}

export const WidgetConfigLibraryReadResponse: FunctionComponent<IWidgetConfigLibraryReadResponseProps> = (
  props: IWidgetConfigLibraryReadResponseProps,
) => {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const [links, setLinks] = useState<ResourceLink[]>([]);
  const [lists, setLists] = useState<ResourceList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedLink, setSelectedLink] = useState<ResourceLink | undefined>(undefined);
  const [newLinks, setNewLinks] = useState<ResourceLink[]>([]);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetLibraryReadResponseConfig => {
    const newConfig = new WidgetLibraryReadResponseConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;

      setIsLoading(true);

      await appContext.globalDataCache.lists.getItems(); //make sure the lists are loaded
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let links = await apiGetLinks(false, accessToken, appContext.globalDataCache);
      let lists = await appContext.globalDataCache.lists.getItems();

      //sort lists on name
      lists = lists
        .filter(
          (l) => l.listType === ResourceListType.DocumentLibrary || l.listType === ResourceListType.SitePageLibrary,
        )
        .sort((a: ResourceList, b: ResourceList) => {
          return sortOnString(a.name, b.name);
        });

      //sort on list name to divide in groups, within the group on link name
      links = links
        .filter(
          (l) =>
            l.list.listType === ResourceListType.DocumentLibrary ||
            l.list.listType === ResourceListType.SitePageLibrary,
        )
        .sort((a: ResourceLink, b: ResourceLink) => {
          if (a.listId !== b.listId) return a.list.name.localeCompare(b.list.name);

          return sortOnString(a.linkName, b.linkName);
        });

      setLists(lists);

      //now set the current config
      const config = loadConfig();
      const selectedLink = links.find((l) => config.linkId === l.linkId);
      setSelectedLink(selectedLink);

      //and filter out the current config from the main list
      setLinks(links.filter((l) => selectedLink?.linkId !== l.linkId));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: IColumn[] = [
    {
      isMultiline: true,
      key: 'linkName',
      name: '',
      minWidth: 100,
      maxWidth: 600,
      isSorted: true,
      isSortedDescending: false,
      isResizable: true,
      onRender: (item?: ResourceLink, index?: number, column?: IColumn) => {
        if (!item) return;

        return <IPText>{item.linkName}</IPText>;
      },
    },
    {
      key: 'delete',
      name: '',
      minWidth: 20,
      maxWidth: 20,
      onRender: (item?: ResourceLink, index?: number, column?: IColumn) => {
        if (!item || !hasUserFeatureGenericManager(appContext)) return;

        return (
          <IPIconButton
            disabled={isLoading}
            iconProps={deleteIcon}
            onClick={() => {
              if (!item) return;
              onRemove(item);
            }}
            styles={{ root: { height: 18 } }}
          />
        );
      },
    },
  ];

  const onRemove = (item: ResourceLink) => {
    setSelectedLink(undefined);
    const newLinks = [...links, item];
    setLinks(newLinks);
  };

  const getConfig = (item: ResourceLink): WidgetLibraryReadResponseConfig => {
    const config = new WidgetLibraryReadResponseConfig();
    config.linkId = item.linkId;

    return config;
  };

  useEffect(() => {
    if (selectedLink) {
      const config = getConfig(selectedLink);
      props.onUpdateConfig(JSON.stringify(config), selectedLink !== undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLink]);

  useEffect(() => {
    if (newLinks && newLinks.length > 0) {
      const link = newLinks[0];
      setSelectedLink(link);
      const filteredLinks = links.filter((l) => link.linkId !== l.linkId);
      setLinks(filteredLinks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newLinks]);

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPLabel>
          {selectedLink
            ? t('widgets:LibraryReadResponse.Config.SelectedLinksLabel')
            : t('widgets:LibraryReadResponse.Config.SelectedLinksLabelEmpty')}
        </IPLabel>
      </IPStack.Item>
      {selectedLink && (
        <IPStack.Item>
          <IPDetailsList
            columns={columns}
            compact
            layoutMode={IPDetailsListLayoutMode.justified}
            items={[selectedLink]}
            selectionMode={IPSelectionMode.none}
            isHeaderVisible={false}
          />
        </IPStack.Item>
      )}
      {!selectedLink && (
        <IPStack.Item grow>
          <LibraryPicker
            multiSelect={false}
            onSelect={setNewLinks}
            links={links}
            lists={lists}
            isLoading={isLoading}
            showHeader={false}
            disallowVirtual={true}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};
